import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { eventsApi, analyticsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket, useRealtimeEvent } from '@/context/WebSocketContext';
import { utcDate } from '@/lib/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, CheckCircle, SprayCan, Search,
  Shield, Bell, Loader2, RefreshCw, LogOut, Volume2, VolumeX, GraduationCap
} from 'lucide-react';
import { playVapeAlarm, playSuspectAlarm, isSoundEnabled, toggleSound, requestNotificationPermission, showBrowserNotification } from '@/lib/alarm';
import { PushBanner } from '@/components/PushBanner';
import './StaffAlarmView.css';

const severityConfig = {
  high: { label: 'HÖG', class: 'severity-high' },
  medium: { label: 'MEDEL', class: 'severity-medium' },
  low: { label: 'LÅG', class: 'severity-low' },
};

// ─── Screen Wake Lock ───
function useWakeLock() {
  const wakeLockRef = useRef(null);

  const request = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch {}
  }, []);

  const release = useCallback(async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch {}
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    request();
    // Re-acquire on visibility change (e.g., tab switch back)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') request();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      release();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [request, release]);
}

// ─── Alarm Card ───
function AlarmCard({ event, onAction, actionLoading, isNew }) {
  const sev = severityConfig[event.severity] || severityConfig.medium;
  const isLoading = actionLoading === event.id;

  return (
    <div
      className={`alarm-card ${sev.class} ${isNew ? 'alarm-card-new' : ''}`}
      data-testid={`staff-alarm-${event.id}`}
    >
      <div className="alarm-card-top">
        <span className={`alarm-sev-badge ${sev.class}`}>{sev.label}</span>
        <span className="alarm-card-time">
          {format(utcDate(event.created_at), 'HH:mm')}
        </span>
      </div>

      <div className="alarm-card-location">
        <h2 className="alarm-room" data-testid={`alarm-room-${event.id}`}>
          {event.room_name || 'Okänt rum'}
        </h2>
        <p className="alarm-school">{event.school_name || ''}</p>
      </div>

      <div className="alarm-score-row">
        <span className="alarm-score-label">Konfidens</span>
        <div className="alarm-score-bar-bg">
          <div
            className={`alarm-score-bar-fill ${sev.class}`}
            style={{ width: `${Math.min(100, Math.round((event.score || 0) * 100))}%` }}
          />
        </div>
        <span className="alarm-score-pct">
          {Math.round((event.score || 0) * 100)}%
        </span>
      </div>

      <div className="alarm-actions" data-testid={`alarm-actions-${event.id}`}>
        <button
          className="alarm-btn alarm-btn-confirm"
          onClick={() => onAction(event.id, 'confirm')}
          disabled={isLoading}
          data-testid={`staff-confirm-${event.id}`}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
          <span>Bekräfta vape</span>
        </button>
        <button
          className="alarm-btn alarm-btn-spray"
          onClick={() => onAction(event.id, 'spray')}
          disabled={isLoading}
          data-testid={`staff-spray-${event.id}`}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <SprayCan className="h-5 w-5" />}
          <span>Spray (ej vape)</span>
        </button>
        <button
          className="alarm-btn alarm-btn-nothing"
          onClick={() => onAction(event.id, 'nothing')}
          disabled={isLoading}
          data-testid={`staff-nothing-${event.id}`}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          <span>Inget hittades</span>
        </button>
      </div>
    </div>
  );
}

export default function StaffAlarmView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [newEventIds, setNewEventIds] = useState(new Set());
  const [showFlash, setShowFlash] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => isSoundEnabled());
  const [calibration, setCalibration] = useState(null);
  const [audioActivated, setAudioActivated] = useState(() => {
    return localStorage.getItem('vapelarm_audio_activated') === 'true';
  });
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightId = searchParams.get('alarm');

  // Request notification permission on mount
  useEffect(() => { requestNotificationPermission(); }, []);

  // Show activation banner on mount
  const activateAudio = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.001;
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch {}
    setAudioActivated(true);
    localStorage.setItem('vapelarm_audio_activated', 'true');
    requestNotificationPermission();
  }, []);

  // Keep screen awake
  useWakeLock();

  const loadAlarms = useCallback(async () => {
    try {
      const res = await eventsApi.list({ status: 'open' });
      const alarms = res.data.filter(e =>
        e.event_type === 'vape' || e.event_type === 'suspect'
      );
      setEvents(alarms);
    } catch {
      toast.error('Kunde inte ladda larm');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAlarms(); }, [loadAlarms]);

  // Auto-refresh: visibilitychange + focus + periodic polling fallback
  // iOS PWA kills WebSocket in background — polling ensures alarms always show up
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadAlarms();
        if (audioActivated) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            ctx.resume();
          } catch {}
        }
      }
    };
    const handleFocus = () => loadAlarms();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    // Polling fallback every 15 seconds
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadAlarms();
      }
    }, 15000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      clearInterval(pollInterval);
    };
  }, [loadAlarms, audioActivated]);

  // Load calibration stats for feedback nudge
  useEffect(() => {
    analyticsApi.myAccuracy()
      .then(res => setCalibration(res.data))
      .catch(() => {});
  }, []);

  // Listen for new events via WebSocket
  const handleNewEvent = useCallback((data) => {
    const event = data?.event;
    if (!event || (event.event_type !== 'vape' && event.event_type !== 'suspect')) return;

    // Add to list immediately
    loadAlarms();

    // Track as new (for animation)
    setNewEventIds(prev => new Set(prev).add(event.id));
    setTimeout(() => {
      setNewEventIds(prev => {
        const next = new Set(prev);
        next.delete(event.id);
        return next;
      });
    }, 5000);

    // Flash overlay
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 2000);

    // ALARM: Sound + Vibration (loud!)
    if (soundEnabled) {
      if (event.event_type === 'vape' || event.severity === 'high') {
        playVapeAlarm();
      } else {
        playSuspectAlarm();
      }
    }

    // Browser push notification
    showBrowserNotification(event);
  }, [loadAlarms, soundEnabled]);

  useRealtimeEvent('new_event', handleNewEvent);

  // Scroll to highlighted alarm from push notification
  useEffect(() => {
    if (highlightId && events.length > 0) {
      const el = document.querySelector(`[data-testid="staff-alarm-${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('alarm-card-highlight');
        setTimeout(() => el.classList.remove('alarm-card-highlight'), 3000);
      }
    }
  }, [highlightId, events]);

  const feedbackMsg = 'Din feedback gör systemet smartare!';

  const handleAction = async (eventId, action) => {
    setActionLoading(eventId);
    try {
      if (action === 'confirm') {
        await eventsApi.confirmVape(eventId);
        toast.success(`Vape bekräftad — tack! ${feedbackMsg}`, { duration: 4000 });
      } else if (action === 'spray') {
        await eventsApi.markSpray(eventId);
        toast.success(`Markerad som spray — tack! ${feedbackMsg}`, { duration: 4000 });
      } else if (action === 'nothing') {
        await eventsApi.nothingFound(eventId);
        toast.success(`Inget hittades — tack! ${feedbackMsg}`, { duration: 4000 });
      }
      setEvents(prev => prev.filter(e => e.id !== eventId));
      analyticsApi.myAccuracy()
        .then(res => setCalibration(res.data))
        .catch(() => {});
    } catch {
      toast.error('Något gick fel');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSound = () => {
    const nowEnabled = toggleSound();
    setSoundEnabled(nowEnabled);
  };

  return (
    <div className="staff-view" data-testid="staff-alarm-view">
      {/* Red flash overlay for new alarms */}
      {showFlash && <div className="alarm-flash-overlay" />}

      {/* Audio activation banner — required for mobile browsers */}
      {!audioActivated && (
        <button
          onClick={activateAudio}
          className="w-full py-4 bg-navy text-white font-ibm font-bold text-lg flex items-center justify-center gap-3 animate-pulse"
          data-testid="activate-audio-btn"
        >
          <Bell className="h-6 w-6" />
          Tryck här för att aktivera larmljud och vibrationer
        </button>
      )}

      {/* Push notification banner */}
      <PushBanner />

      {/* Header */}
      <header className="staff-header">
        <div className="staff-header-left">
          <Shield className="h-6 w-6 text-white" />
          <h1 className="staff-title">Vapelarm Cloud</h1>
        </div>
        <div className="staff-header-right">
          <button
            onClick={handleToggleSound}
            className={`staff-sound-toggle ${soundEnabled ? 'sound-on' : 'sound-off'}`}
            data-testid="staff-sound-toggle"
            title={soundEnabled ? 'Ljud på' : 'Ljud av'}
          >
            {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </button>
          <button onClick={loadAlarms} className="staff-refresh" data-testid="staff-refresh-btn">
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => navigate('/academy')} className="staff-refresh" data-testid="staff-academy-btn" title="Vapelarm Academy">
            <GraduationCap className="h-5 w-5" />
          </button>
          <button onClick={logout} className="staff-logout" data-testid="staff-logout-btn">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Greeting + alarm count */}
      <div className="staff-greeting">
        <p className="staff-hello">Hej {user?.name || 'personal'}!</p>
        <p className="staff-count">
          {events.length === 0
            ? 'Inga aktiva larm just nu'
            : `${events.length} aktiv${events.length > 1 ? 'a' : 't'} larm`}
        </p>
        {calibration && (
          <div className="staff-calibration-nudge" data-testid="staff-calibration-nudge">
            <span className="staff-calibration-accuracy" data-testid="staff-accuracy">
              {calibration.is_active
                ? `Skolans träffsäkerhet: ${Math.round(calibration.accuracy * 100)}%`
                : 'Skolans träffsäkerhet: För lite underlag ännu'}
            </span>
            <span className="staff-calibration-hint">
              {calibration.is_active
                ? `Baserat på ${calibration.total_feedback} feedback-svar från skolan — varje svar förbättrar detekteringen`
                : 'Svara på larmen för att förbättra detekteringen'}
            </span>
          </div>
        )}
      </div>

      {/* Alarm list */}
      <div className="staff-alarm-list">
        {loading ? (
          <div className="staff-loading">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : events.length === 0 ? (
          <div className="staff-empty" data-testid="staff-no-alarms">
            <div className="staff-empty-icon">
              <Bell className="h-12 w-12 text-slate-300" />
            </div>
            <p className="staff-empty-title">Allt lugnt</p>
            <p className="staff-empty-sub">
              Inga aktiva larm. Du meddelas automatiskt med ljud och vibration.
            </p>
          </div>
        ) : (
          events.map(event => (
            <AlarmCard
              key={event.id}
              event={event}
              onAction={handleAction}
              actionLoading={actionLoading}
              isNew={newEventIds.has(event.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
